import { CapTableVersion } from 'common/types/company';
import { ValuationByAllocation, ValuationsApproach } from 'common/types/valuation';
import {
  VALUATIONS_BACKSOLVE_APPROACH,
  VALUATIONS_FUTURE_EXIT_APPROACH,
  VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { CompanyPdfOptions } from '../PdfExportConfigDialog.types';
import { getApproachExportItemId, isApproachItemSelected } from './getApproachExportItems';

interface GetReferencedCapTableVersionsArgs {
  valuationInfo?: ValuationByAllocation | null;
  capTableVersions?: CapTableVersion[] | null;
  companyOptions: CompanyPdfOptions;
  approachOptions: Record<string, boolean>;
}

const isLiveApproach = (approach: ValuationsApproach) => !approach.is_deleted;

/**
 * Every cap table version the exported valuation actually uses, plus the primary.
 *
 * Approach-sourced references follow the dialog selection — unchecking Backsolve drops the
 * version only that Backsolve referenced. Allocation-scenario references always count: they
 * back the concluded values whichever approach pages are exported.
 *
 * Versions the backend reports as `is_empty` (no live securities) are dropped, primary
 * included: they can only ever produce a header-only blank page.
 *
 * Returns [] both when the version list has not resolved and when every referenced version is
 * empty. The caller tells the two apart by the version list itself — unresolved keeps the
 * single-page fallback, resolved-but-all-empty emits no pages.
 */
const getReferencedCapTableVersions = ({
  valuationInfo,
  capTableVersions,
  companyOptions,
  approachOptions,
}: GetReferencedCapTableVersionsArgs): CapTableVersion[] => {
  if (!capTableVersions?.length) {
    return [];
  }

  const referencedIds = new Set<number>();
  const addId = (id?: number | null) => {
    if (typeof id === 'number') {
      referencedIds.add(id);
    }
  };

  valuationInfo?.allocation?.allocation_scenarios?.forEach(scenario => {
    if (scenario.is_deleted) {
      return;
    }
    addId(scenario.cap_table_id ?? scenario.cap_table?.id);
  });

  const approaches = valuationInfo?.valuations_approaches?.filter(isLiveApproach) ?? [];

  // The Market Adjustment ledger is part of its Backsolve, so exporting either row keeps
  // that Backsolve's cap table versions in scope. Each Backsolve is decided on its own.
  approaches
    .filter(approach => approach.approach_type === VALUATIONS_BACKSOLVE_APPROACH)
    .forEach(approach => {
      const approachKey = String(approach.id ?? approach.panelId);
      const isMainSelected = isApproachItemSelected(approachOptions, getApproachExportItemId(approachKey, 'main'));
      const isLedgerSelected =
        Boolean(approach.valuations_approach_backsolve?.adjust_for_market)
        && isApproachItemSelected(approachOptions, getApproachExportItemId(approachKey, 'marketAdjustment'));
      if (!isMainSelected && !isLedgerSelected) {
        return;
      }
      approach.valuations_approach_backsolve?.applied_methodologies?.forEach(methodology => {
        if (!methodology.is_deleted) {
          addId(methodology.cap_table);
        }
      });
    });

  if (companyOptions.approachFutureExit) {
    approaches
      .filter(approach => approach.approach_type === VALUATIONS_FUTURE_EXIT_APPROACH)
      .forEach(approach => addId(approach.valuations_approach_future_exit?.cap_table));
  }

  if (companyOptions.approachSpecifiedShareValues) {
    approaches
      .filter(approach => approach.approach_type === VALUATIONS_SPECIFIED_SHARE_VALUES_APPROACH)
      .forEach(approach => addId(approach.valuations_approach_ssv?.cap_table));
  }

  const selected = capTableVersions.filter(
    version =>
      !version.is_empty
      && (version.is_primary || (typeof version.id === 'number' && referencedIds.has(version.id)))
  );

  // Primary first so page order stays stable; the rest keep the endpoint's order.
  return selected.sort((a, b) => Number(Boolean(b.is_primary)) - Number(Boolean(a.is_primary)));
};

export default getReferencedCapTableVersions;
